import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStoryStore } from '../stores/storyStore'
import { AIService } from '../services/aiService'
import StoryCreationFlow from '../components/StoryCreationFlow'
import AIGuidedCreation from '../components/AIGuidedCreation'
import toast from 'react-hot-toast'

export const StoryGenerator = () => {
  const navigate = useNavigate()
  const { createStory, stories } = useStoryStore()
  const [storyContent, setStoryContent] = useState('')
  const [showCreationFlow, setShowCreationFlow] = useState(false)
  const [creationMode, setCreationMode] = useState('intelligent') // 'auto' 或 'intelligent'，默认智能引导
  const [editStoryId, setEditStoryId] = useState(null) // 编辑模式的故事ID
  const [createType, setCreateType] = useState('story') // 'story', 'character', 'world'，默认创建故事
  
  // 检查URL中是否有编辑标记或导入标记
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const editId = urlParams.get('edit')
    const importParam = urlParams.get('import')
    
    if (editId) {
      setEditStoryId(editId)
      setShowCreationFlow(true)
      setCreationMode('intelligent')
      
      // 清除URL参数
      window.history.replaceState({}, '', '/')
    } else if (importParam === 'playcreate') {
      // 检查是否有玩创模式转换的数据
      const convertedData = sessionStorage.getItem('playcreate-converted-data')
      if (convertedData) {
        setShowCreationFlow(true)
        setCreationMode('intelligent')
        // 清除URL参数
        window.history.replaceState({}, '', '/')
      }
    }
  }, [])

  const handleStartCreation = async () => {
    if (!storyContent.trim()) {
      toast.error('请输入你的创意想法')
      return
    }

    // 根据创建类型和模式进行处理
    if (createType === 'story') {
      // 创建故事：智能引导模式不需要选择题材和风格，AI会分析并给出选项
      // 自动生成模式也不需要，会使用默认值
      setShowCreationFlow(true)
    } else if (createType === 'character') {
      // 创建角色
      if (creationMode === 'intelligent') {
        // 智能引导模式：跳转到角色创建页面，传递描述
        sessionStorage.setItem('character-creation-description', storyContent)
        navigate('/play-create')
      } else {
        // 自动生成模式：调用AI自动生成角色
        await handleAutoCreateCharacter()
      }
    } else if (createType === 'world') {
      // 创建世界
      if (creationMode === 'intelligent') {
        // 智能引导模式：跳转到世界创建页面，传递描述
        sessionStorage.setItem('world-creation-description', storyContent)
        navigate('/world-simulator/create')
      } else {
        // 自动生成模式：调用AI自动生成世界
        await handleAutoCreateWorld()
      }
    }
  }

  // 自动生成角色
  const handleAutoCreateCharacter = async () => {
    try {
      toast.loading('正在自动生成角色...', { id: 'creating-character' })
      
      // 调用AI生成角色
      const { RealAIService } = await import('../services/realAiService')
      const prompt = `根据以下描述，自动生成一个完整的角色设定：

用户描述：${storyContent}

请生成一个完整的角色，包含以下信息：
1. 角色名称
2. 性格特点
3. 背景故事
4. 外观描述
5. 说话风格
6. 兴趣爱好
7. 系统提示词（用于AI对话）

请以JSON格式返回：
{
  "name": "角色名称",
  "personality": "性格特点",
  "description": "背景故事",
  "appearance": "外观描述",
  "speechStyle": "说话风格",
  "interests": ["兴趣1", "兴趣2"],
  "systemPrompt": "系统提示词"
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      // 解析JSON响应
      let characterData
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          characterData = JSON.parse(jsonMatch[0])
        } else {
          throw new Error('无法解析AI响应')
        }
      } catch (e) {
        // 如果解析失败，使用默认值
        characterData = {
          name: storyContent.split('，')[0] || '新角色',
          personality: '友好、开朗',
          description: storyContent,
          appearance: '神秘而引人注目',
          speechStyle: '自然、友好',
          interests: [],
          systemPrompt: `你是一个名为${storyContent.split('，')[0] || '新角色'}的虚拟角色。${storyContent}`
        }
      }

      // 生成标签
      const TagGeneratorService = (await import('../services/tagGeneratorService')).default
      let tags = []
      try {
        tags = await TagGeneratorService.generateCharacterTags({
          name: characterData.name || '新角色',
          description: characterData.description || storyContent,
          personality: characterData.personality || '友好、开朗',
          background: characterData.appearance || '',
          interests: characterData.interests || []
        })
        console.log('🏷️ 已生成角色标签:', tags)
      } catch (error) {
        console.warn('⚠️ 标签生成失败，使用默认标签:', error)
        tags = ['原创', '推荐']
      }
      
      // 创建角色对象
      const character = {
        id: `char-${Date.now()}`,
        name: characterData.name || '新角色',
        description: characterData.description || storyContent,
        personality: characterData.personality || '友好、开朗',
        avatar: '',
        background: characterData.appearance || '',
        systemPrompt: characterData.systemPrompt || `你是一个名为${characterData.name || '新角色'}的虚拟角色。性格特点：${characterData.personality || '友好、开朗'}。${characterData.description || storyContent}`,
        keyTraits: characterData.interests || [],
        interests: characterData.interests || [],
        speechStyle: characterData.speechStyle || '自然、友好',
        tags,
        enableAdultMode: false,
        status: 'draft', // 默认状态为草稿
        isPublic: false // 默认未发布
      }

      // 保存角色
      const playCreateStorageService = (await import('../services/playCreateStorageService')).default
      await playCreateStorageService.saveCharacterProfile(character)
      await playCreateStorageService.saveCharacterState(character.id, {
        favorability: 0,
        firstChatTime: Date.now(),
        lastChatTime: Date.now(),
        totalChatCount: 0,
        currentLevel: '陌生'
      })

      toast.success('角色创建成功！', { id: 'creating-character' })
      
      // 跳转到角色聊天页面
      navigate(`/play-create/chat/${character.id}`)
    } catch (error) {
      console.error('自动创建角色失败:', error)
      toast.error('创建角色失败：' + error.message, { id: 'creating-character' })
    }
  }

  // 自动生成世界
  const handleAutoCreateWorld = async () => {
    try {
      toast.loading('正在自动生成世界...', { id: 'creating-world' })
      
      // 调用AI生成世界
      const { RealAIService } = await import('../services/realAiService')
      const worldSimulatorStorageService = (await import('../services/worldSimulatorStorageService')).default
      
      const prompt = `根据以下描述，自动生成一个完整的世界模拟器设定：

用户描述：${storyContent}

请生成一个完整的世界，包含以下信息：
1. 世界名称
2. 世界描述
3. 世界概要
4. 世界观设定（preText）
5. 系统与规则
6. 量化系统（至少2个）
7. 主要地区（3-5个）
8. 重要角色（3-5个）

请以JSON格式返回：
{
  "name": "世界名称",
  "description": "世界描述",
  "summary": "世界概要",
  "preText": "详细的世界观设定",
  "postText": "AI核心指令集",
  "systems": {},
  "quantitativeSystems": [{"name": "系统名", "initial": 100}],
  "regions": [{"name": "地区名", "description": "描述"}],
  "characters": [{"name": "角色名", "role": "职业", "description": "描述"}]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      // 解析JSON响应
      let worldData
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          worldData = JSON.parse(jsonMatch[0])
        } else {
          throw new Error('无法解析AI响应')
        }
      } catch (e) {
        // 如果解析失败，使用默认值
        worldData = {
          name: storyContent.split('，')[0] || '新世界',
          description: storyContent,
          summary: storyContent.substring(0, 100),
          preText: storyContent,
          postText: '',
          systems: {},
          quantitativeSystems: [
            { name: '生命值', initial: 100 },
            { name: '能量值', initial: 100 }
          ],
          regions: [
            { name: '起始地区', description: '世界的起始地点' }
          ],
          characters: [
            { name: '引导NPC', role: '向导', description: '帮助玩家的角色' }
          ]
        }
      }

      // 创建世界对象
      const world = {
        id: `world-${Date.now()}`,
        name: worldData.name || '新世界',
        description: worldData.description || storyContent,
        summary: worldData.summary || storyContent.substring(0, 100),
        preText: worldData.preText || storyContent,
        postText: worldData.postText || '',
        prePrompt: '',
        worldBook: [],
        systems: worldData.systems || {},
        quantitativeSystems: worldData.quantitativeSystems || [],
        characters: worldData.characters || [],
        regions: worldData.regions || [],
        language: 'zh-Hans',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      // 保存世界
      await worldSimulatorStorageService.saveWorld(world)

      toast.success('世界创建成功！', { id: 'creating-world' })
      
      // 跳转到世界详情页面
      navigate(`/world-simulator/detail/${world.id}`)
    } catch (error) {
      console.error('自动创建世界失败:', error)
      toast.error('创建世界失败：' + error.message, { id: 'creating-world' })
    }
  }

  const handleStoryCreated = (newStory) => {
    toast.success('创作开始！')
    navigate(`/editor/${newStory.id}`)
  }

  const handleEditStory = (storyId) => {
    console.log('StoryGenerator: handleEditStory 被调用，storyId:', storyId)
    console.log('准备跳转到:', `/editor/${storyId}`)
    navigate(`/editor/${storyId}`)
    console.log('跳转命令已执行')
  }

  // 粒子背景效果
  useEffect(() => {
    const createParticles = () => {
      const particlesContainer = document.getElementById('particles')
      if (!particlesContainer) return

      const particleCount = 50
      
      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div')
        
        // 随机大小和位置
        const size = Math.random() * 3 + 1
        const posX = Math.random() * 100
        const posY = Math.random() * 100
        const delay = Math.random() * 5
        const duration = Math.random() * 20 + 10
        
        // 设置样式
        particle.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          background: rgba(6, 182, 212, 0.6);
          border-radius: 50%;
          top: ${posY}%;
          left: ${posX}%;
          box-shadow: 0 0 ${size * 3}px rgba(6, 182, 212, 0.8);
          animation: float ${duration}s ease-in-out ${delay}s infinite;
        `
        
        particlesContainer.appendChild(particle)
      }
    }

    createParticles()
  }, [])

  return (
    <div className="bg-dark text-gray-100 min-h-screen font-sans overflow-x-hidden">
      {/* 粒子背景效果 (固定定位) */}
      <div id="particles" className="fixed inset-0 z-0 opacity-30 pointer-events-none"></div>

      {/* 主内容区 */}
      <main className="pt-16">
        {showCreationFlow && createType === 'story' ? (
          creationMode === 'intelligent' ? (
            <AIGuidedCreation 
              userDescription={storyContent}
              onClose={() => {
                setShowCreationFlow(false)
                setEditStoryId(null)
              }}
              editStoryId={editStoryId}
            />
          ) : (
            <StoryCreationFlow 
              onStoryCreated={handleStoryCreated}
              onEditStory={handleEditStory}
              initialContent={storyContent}
              initialGenre={''}
              initialStyle={''}
            />
          )
        ) : (
          /* 首页创作入口 */
          <section className="relative h-[90vh] min-h-[600px] overflow-hidden">
            {/* 淡格子背景底层 */}
            <div className="absolute inset-0 bg-grid-purple opacity-20">
              {/* 深色背景覆盖 */}
              <div className="absolute inset-0 bg-dark"></div>
              
              {/* 渐变暗化效果 */}
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/80 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-b from-dark-900 via-dark-900/60 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-l from-dark-900 via-dark-900/70 to-transparent"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/70 to-transparent"></div>
            </div>
            
            {/* 中央创作输入区 */}
            <div className="relative h-full flex flex-col items-center justify-center px-4 z-10">
              <div className="title-container mb-6">
                <h2 className="adaptive-text font-display font-bold text-center text-shadow-lg responsive-title">
                  <span>释放你的</span>
                  <span className="text-primary brand-glow">创意潜能</span>
                  <span>，构建无限可能</span>
                </h2>
              </div>
              
              {/* 主输入框 */}
              <div className="w-full max-w-3xl bg-dark-light/80 backdrop-blur-md rounded-xl p-1 border border-primary/30 shadow-lg animate-float">
                <div className="flex flex-col md:flex-row gap-2">
                  <input 
                    type="text" 
                    value={storyContent}
                    onChange={(e) => setStoryContent(e.target.value)}
                    placeholder={
                      createType === 'story' 
                        ? '输入你的创意... 例如：一个关于星际旅行的冒险故事，主角发现了...'
                        : createType === 'character'
                        ? '描述你想要创建的角色... 例如：一个温柔善良的魔法师，喜欢帮助他人...'
                        : '描述你想要创建的世界... 例如：一个明末天启年间的历史模拟世界...'
                    }
                    className="flex-1 bg-transparent border-none rounded-lg px-4 py-6 text-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all text-white placeholder-gray-400"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && storyContent.trim()) {
                        handleStartCreation()
                      }
                    }}
                  />
                  <button 
                    onClick={handleStartCreation}
                    disabled={!storyContent.trim()}
                    className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-6 rounded-lg font-medium transition-all animate-glow"
                  >
                    {createType === 'story' ? '开始创作' : createType === 'character' ? '创建角色' : '创建世界'}
                  </button>
                </div>
              </div>
              
              {/* 创作模式选择按钮 */}
              <div className="w-full max-w-3xl mt-6">
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setCreateType('story')
                      setCreationMode('intelligent')
                    }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                      createType === 'story'
                        ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white shadow-lg'
                        : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
                    }`}
                  >
                    <span className="text-xl">📖</span>
                    <span>创建故事</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setCreateType('character')
                      setCreationMode('intelligent')
                    }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                      createType === 'character'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                        : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
                    }`}
                  >
                    <span className="text-xl">👤</span>
                    <span>创建角色</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setCreateType('world')
                      setCreationMode('intelligent')
                    }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                      createType === 'world'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                        : 'bg-dark-700/50 text-white/70 hover:text-white hover:bg-dark-700 border border-dark-600'
                    }`}
                  >
                    <span className="text-xl">🌍</span>
                    <span>创建世界</span>
                  </button>
                </div>
              </div>
              
              {/* 模式选择（所有类型都显示） */}
              <div className="w-full max-w-3xl mt-4">
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setCreationMode('intelligent')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      creationMode === 'intelligent'
                        ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white shadow-lg'
                        : 'text-white/70 hover:text-white hover:bg-white/10 bg-dark-700/50'
                    }`}
                    title={
                      createType === 'story' 
                        ? '🌟 增强版智能引导：真实AI分析、流式呈现、专业互动建议、完整对话记录'
                        : createType === 'character'
                        ? '智能引导：通过对话逐步完善角色设定'
                        : '智能引导：通过对话逐步构建完整世界'
                    }
                  >
                    🤖 智能引导
                  </button>
                  <button
                    onClick={() => setCreationMode('auto')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      creationMode === 'auto'
                        ? 'bg-primary text-white'
                        : 'text-white/70 hover:text-white hover:bg-white/10 bg-dark-700/50'
                    }`}
                    title={
                      createType === 'story' 
                        ? 'AI自动生成完整故事，快速便捷'
                        : createType === 'character'
                        ? 'AI根据描述自动生成完整角色'
                        : 'AI根据描述自动生成完整世界'
                    }
                  >
                    自动生成
                  </button>
                </div>
                
                {/* 创作模式提示 */}
                <div className="mt-4">
                  <div className="bg-dark/30 backdrop-blur-md rounded-lg p-4 border border-white/5">
                    <div className="flex items-center justify-center text-sm">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${
                          creationMode === 'intelligent' ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500' :
                          creationMode === 'auto' ? 'bg-primary' : 
                          'bg-gradient-to-r from-purple-500 to-pink-500'
                        }`}></div>
                        <span className="text-gray-300">
                          {createType === 'story' && creationMode === 'intelligent'
                            ? '🌟 增强版智能引导：真实AI分析 | 流式呈现思考过程 | 专业互动内容建议（对话/分支/多结局/情绪） | 完整对话记录 | 对齐编辑器结构'
                            : createType === 'story' && creationMode === 'auto'
                            ? '自动生成：AI一次性生成完整故事，快速便捷'
                            : createType === 'character' && creationMode === 'intelligent'
                            ? '智能引导：通过AI对话逐步完善角色设定，包括姓名、性格、背景等'
                            : createType === 'character' && creationMode === 'auto'
                            ? '自动生成：AI根据你的描述自动生成完整的角色设定'
                            : createType === 'world' && creationMode === 'intelligent'
                            ? '智能引导：通过AI对话逐步构建完整世界，包括世界观、规则、地区、角色等'
                            : createType === 'world' && creationMode === 'auto'
                            ? '自动生成：AI根据你的描述自动生成完整的世界设定'
                            : ''
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  )
}
